import { isBaseTabUrl } from './TabLink';

const HIGHLIGHT_CLASS = 'is-search-target';
const HIGHLIGHT_DURATION_MS = 2500;
const FIND_RETRY_MS = 100;
const FIND_TIMEOUT_MS = 6000;
const SETTLE_DURATION_MS = 1800;
const SETTLE_TOLERANCE_PX = 4;
const SETTLE_STABLE_FRAMES = 4;
const DEFAULT_OFFSET_PX = 32;

const LATE_RENDER_CONTAINERS =
	'article, li, .jet-listing-grid__item, .jet-listing-dynamic-post, .elementor-post, .wp-block-post-template li, .wp-block-columns, .wp-block-column, .wp-block-group';

const disableScrollRestoration = () => {
	if (!('scrollRestoration' in window.history)) return;
	try {
		window.history.scrollRestoration = 'manual';
	} catch {}
};

const findTarget = (id) => {
	const byId = document.getElementById(id);
	if (byId) return byId;

	const postIdMatch = id.match(/^post-(\d+)$/);
	if (postIdMatch) {
		const numeric = postIdMatch[1];
		return (
			document.querySelector(`[data-post-id="${numeric}"]`) ||
			document.querySelector(`.post-${numeric}`)
		);
	}

	const escaped = id.replace(/"/g, '\\"');
	const link = document.querySelector(
		`a[href$="/${escaped}/"], a[href$="/${escaped}"], a[href*="/${escaped}/?"], a[href*="/${escaped}#"]`
	);
	if (!link) return null;

	return link.closest(LATE_RENDER_CONTAINERS) || link;
};

const waitForTarget = (id, onFound) => {
	const deadline = Date.now() + FIND_TIMEOUT_MS;

	const attempt = () => {
		const target = findTarget(id);
		if (target) return onFound(target);
		if (Date.now() < deadline) setTimeout(attempt, FIND_RETRY_MS);
	};

	attempt();
};

const getOffset = () => {
	const adminBar = document.getElementById('wpadminbar');
	return (adminBar?.offsetHeight ?? 0) + DEFAULT_OFFSET_PX;
};

const computeTop = (target) =>
	Math.max(
		0,
		Math.round(target.getBoundingClientRect().top + window.scrollY - getOffset())
	);

const highlight = (id, initialTarget) => {
	initialTarget.classList.add(HIGHLIGHT_CLASS);
	setTimeout(() => {
		const current = findTarget(id) || initialTarget;
		current?.classList.remove(HIGHLIGHT_CLASS);
	}, HIGHLIGHT_DURATION_MS);
};

const settleTo = (id, initialTarget) => {
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const deadline = Date.now() + SETTLE_DURATION_MS;
	let stableFrames = 0;
	let firstPass = true;

	highlight(id, initialTarget);

	const step = () => {
		const target = findTarget(id) || initialTarget;
		if (!target?.isConnected) return;

		const desired = computeTop(target);
		const diff = Math.abs(window.scrollY - desired);

		if (diff > SETTLE_TOLERANCE_PX) {
			window.scrollTo({
				top: desired,
				behavior: firstPass && !reduceMotion ? 'smooth' : 'auto',
			});
			stableFrames = 0;
		} else {
			stableFrames += 1;
		}

		firstPass = false;

		if (stableFrames < SETTLE_STABLE_FRAMES && Date.now() < deadline) {
			requestAnimationFrame(step);
		}
	};

	requestAnimationFrame(step);
};

const scrollToCurrentHash = () => {
	const hash = window.location.hash;
	if (!hash || hash.length < 2 || isBaseTabUrl()) return;

	const id = decodeURIComponent(hash.slice(1));
	waitForTarget(id, (target) => settleTo(id, target));
};

const ArchiveHashScroll = {
	init() {
		disableScrollRestoration();

		// Re-run on same-document hash changes (e.g. clicking a search result
		// that points to an anchor on the page you're already viewing). Native
		// hash jumps fail for listing items that only expose data-post-id, so we
		// handle the scroll ourselves.
		window.addEventListener('hashchange', scrollToCurrentHash);

		if (!window.location.hash || window.location.hash.length < 2) return;

		if (document.readyState === 'complete') {
			scrollToCurrentHash();
		} else {
			window.addEventListener('load', scrollToCurrentHash, { once: true });
		}
	},
};

export default ArchiveHashScroll;
